import { useEffect, useMemo, useState } from "react";
import { vertebraGroups } from "../../../classes/enums/AnnotationsEnum";
import { ButtonSize } from "../../../classes/enums/ButtonEnum";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";
import ColoredRangeSlider from "../../../components/coloredRangeSlider/ColoredRangeSlider";
import IconButton from "../../../components/iconButton/IconButton";
import { TooltipOrientation } from "../../../components/tooltip/Tooltip";
import WidgetBox from "../../../components/widgetBox/WidgetBox";
import ValidationFlag from "../../validation/validationFlag/ValidationFlag";
import { SPINESIM_MODALITIES } from "../../../classes/enums/MedicalImagesEnum";
import "./RadioDensityStatistics.css";

const THORACIC_THRESHOLD = 147.0;
const LUMBAR_THRESHOLD = 136.0;

const RadioDensityStatistics = ({
  annotations,
  areViewsReady,
  instances,
  refresh,
  externalStatistics,
  onStatisticsUpdate,
}) => {
  const [statistics, setStatistics] = useState(externalStatistics || null);
  const [minThoracic, setMinThoracic] = useState(THORACIC_THRESHOLD);
  const [minLumbar, setMinLumbar] = useState(LUMBAR_THRESHOLD);
  const [maxThoracic, setMaxThoracic] = useState(2000);
  const [maxLumbar, setMaxLumbar] = useState(2000);

  // Sync with external statistics if provided
  useEffect(() => {
    if (externalStatistics) {
      setStatistics(externalStatistics);
    }
  }, [externalStatistics]);

  // Compute min/max for slider ranges based on statistics
  useEffect(() => {
    if (!statistics) return;

    let thoracicValues = [];
    let lumbarValues = [];

    Object.keys(statistics).forEach(entityIdStr => {
      const entityId = parseInt(entityIdStr);
      const vertebra = vertebraUtils.getVertebraFromId(entityId);
      const stats = statistics[entityId];

      if (stats && stats.mean != null) {
        if (vertebraGroups.THORACIC.includes(vertebra)) {
          thoracicValues.push(stats.mean);
        } else if (vertebraGroups.LUMBAR.includes(vertebra)) {
          lumbarValues.push(stats.mean);
        }
      }
    });

    if (thoracicValues.length > 0) {
      const minThoracicValue = Math.min(...thoracicValues);
      setMinThoracic(Math.max(minThoracicValue - 50, 0));
      const maxThoracicValue = Math.max(...thoracicValues);
      setMaxThoracic(maxThoracicValue + 50);
    }

    if (lumbarValues.length > 0) {
      const minLumbarValue = Math.min(...lumbarValues);
      setMinLumbar(Math.max(minLumbarValue - 50, 0));
      const maxLumbarValue = Math.max(...lumbarValues);
      setMaxLumbar(maxLumbarValue + 50);
    }
  }, [statistics]);

  const tableData = useMemo(() => {
    if (!statistics) return [];

    const rows = Object.keys(statistics).map(entityIdStr => {
      const entityId = parseInt(entityIdStr);
      const stats = statistics[entityId];
      const vertebra = vertebraUtils.getVertebraFromId(entityId);

      return {
        entityId,
        vertebra,
        vertebraLabel: vertebra
          ? vertebraUtils.getVertebraLabel(vertebra)
          : `ID ${entityId}`,
        average: stats.mean != null ? Math.round(stats.mean) : "-",
        min: stats.min != null ? Math.round(stats.min) : "-",
        max: stats.max != null ? Math.round(stats.max) : "-",
      };
    });

    const allVertebrae = [
      ...vertebraGroups.CERVICAL,
      ...vertebraGroups.THORACIC,
      ...vertebraGroups.LUMBAR,
    ];

    rows.sort((a, b) => {
      const indexA = allVertebrae.indexOf(a.vertebra);
      const indexB = allVertebrae.indexOf(b.vertebra);
      return indexA - indexB;
    });

    return rows;
  }, [statistics]);

  const markers = useMemo(() => {
    const thoracicMarkers = [];
    const lumbarMarkers = [];

    if (statistics) {
      Object.keys(statistics).forEach(entityIdStr => {
        const entityId = parseInt(entityIdStr);
        const vertebra = vertebraUtils.getVertebraFromId(entityId);
        const stats = statistics[entityId];

        if (stats && stats.mean != null) {
          const marker = {
            label: vertebra,
            value: Math.round(stats.mean),
            color: "#FFFFFF",
          };

          if (vertebraGroups.THORACIC.includes(vertebra)) {
            thoracicMarkers.push(marker);
          } else if (vertebraGroups.LUMBAR.includes(vertebra)) {
            lumbarMarkers.push(marker);
          }
        }
      });

      return [
        {
          group: "Thoracic",
          markers: thoracicMarkers,
        },
        {
          group: "Lumbar",
          markers: lumbarMarkers,
        },
      ];
    }
    return [];
  }, [statistics]);

  const renderSliders = () => {
    return (
      <div className="radio-density-sliders">
        {markers.map(group => (
          <div className="radio-density-slider-group" key={group.group}>
            <div className="radio-density-slider-label App-text-medium">
              {group.group} Radiodensity
            </div>
            <ColoredRangeSlider
              min={group.group === "Thoracic" ? minThoracic : minLumbar}
              max={group.group === "Thoracic" ? maxThoracic : maxLumbar}
              value={
                group.group === "Thoracic"
                  ? THORACIC_THRESHOLD
                  : LUMBAR_THRESHOLD
              }
              markers={group.markers}
              onChange={() => {}}
              key={group.group}
              mode="biphasic"
              threshold={
                group.group === "Thoracic"
                  ? THORACIC_THRESHOLD
                  : LUMBAR_THRESHOLD
              }
            />
            <div className="radio-density-slider-infos">
              <div className="min-label">Low</div>
              <div className="threshold-label">
                {group.group === "Thoracic"
                  ? THORACIC_THRESHOLD
                  : LUMBAR_THRESHOLD}{" "}
                HU
                <IconButton
                  icon="Info"
                  tooltipContent={
                    <ul>
                      <li>
                        Red color indicates low HU values, with thresholds set
                        at &lt;147 HU for the thoracic spine and &lt;136 HU for
                        the lumbar spine.
                      </li>
                      <li>
                        These thresholds are indicative only and must not be
                        interpreted as diagnostic criteria for osteoporosis or
                        other bone diseases. Clinical diagnosis requires
                        standardized BMD measurements obtained through DXA scans
                        or equivalent validated methods.
                      </li>
                    </ul>
                  }
                  className="spp-info-button"
                  onClick={e => e.stopPropagation()}
                  isCircle
                  size={ButtonSize.MICRO}
                  isLightBlue={false}
                  tooltipOrientation={TooltipOrientation.Left}
                />
              </div>
              <div className="max-label">High</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="App-radio-density-widget">
      <ValidationFlag
        instances={instances}
        refresh={refresh}
        modalityContext={SPINESIM_MODALITIES.CT}
      />
      <div className="radio-density-statistics-container">
        <WidgetBox
          title={"Radiodensity"}
          isExpandable={true}
          isSmall={true}
          noBgColor={true}
          noHorizontalPadding={true}
          toggleTitleColor={true}
          name={`statistics`}
        >
          <div className="radio-density-statistics">
            <div className="radio-density-statistics-description App-text-medium">
              No calibration for Bone Mineral Density (BMD) is performed in this
              analysis. The displayed values are direct Hounsfield Unit (HU)
              measurements obtained from CT scans.{" "}
              <IconButton
                icon="Info"
                title={
                  "HU values represent relative radiodensity calibrated to water, " +
                  "but calibration to water does not ensure accurate correlation to absolute Bone Mineral Density (BMD). " +
                  "HU values can significantly vary depending on scanner settings and operational parameters (including X-ray spectra, scanner calibration, tube voltage, and scanner stability). " +
                  "Hence, thresholds applied based on HU values must always be interpreted cautiously. " +
                  "Users must rely on validated diagnostic methods for clinical decisions concerning osteoporosis and bone mineral density."
                }
                className="spp-info-button"
                onClick={e => e.stopPropagation()}
                isCircle
                size={ButtonSize.MICRO}
                isLightBlue={false}
                tooltipOrientation={TooltipOrientation.Left}
              />
            </div>
            <div className="radio-density-statistics-sliders">
              {renderSliders()}
            </div>
            <div className="radio-density-statistics-table">
              {!statistics ? (
                <div className="radio-density-statistics-loading">
                  Loading statistics...
                </div>
              ) : tableData.length === 0 ? (
                <div className="radio-density-statistics-empty">
                  No statistics available
                </div>
              ) : (
                <table className="radio-density-table">
                  <thead>
                    <tr>
                      <th>Vertebra</th>
                      <th>Average (HU)</th>
                      <th>Min (HU)</th>
                      <th>Max (HU)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map(row => (
                      <tr key={row.entityId}>
                        <td className="vertebra-cell">{row.vertebraLabel}</td>
                        <td className="stat-cell">{row.average}</td>
                        <td className="stat-cell">{row.min}</td>
                        <td className="stat-cell">{row.max}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </WidgetBox>
      </div>
    </div>
  );
};

export default RadioDensityStatistics;
